import React from 'react';
import PropTypes from 'prop-types';
import {i18n, i18nString, get3charMonth} from '../../Utility/Format';
import Icon from '../Helper/Icon';
import {FormattedMessage} from 'react-intl';
import moment from 'moment';
import Dropdown from '../Controls/Dropdown';

/**
 * creates an array of years starting from last year back to 2005
 */
const getYears = (skipThisYear) => {
    let years = [];
    let thisYear = moment().startOf('year');
    if (skipThisYear){
        thisYear.subtract(1, 'year'); // skip the present year
    }

    while (thisYear.isSameOrAfter(moment('2014-01-01'))) {
        years.push(thisYear.format('YYYY'));
        thisYear.subtract(1, 'year')
    }

    return years;
};

/**
 * creates an array of months
 */
const getMonths = (locale, year) => {
    let months = moment.monthsShort();
    moment.locale(locale);
    // if it's the present year, only return past months
    if (moment().isSame(moment().year(year), 'year')){
        let lastMonth = moment().endOf('year');
        const now = moment();
        while (lastMonth.isSameOrAfter(now)){
            months.splice(months.indexOf(lastMonth.format('MMM')), 1);
            lastMonth.subtract(1, 'months');
        }
    }
    return months;
};

/**
 * returns the month as Number for the shortcut returned by getMonths
 */
const getMonthByShortcut = (month, locale) => {
    moment.locale(locale);
    return moment(month, "MMM").format("M");
};

/**
 * This is a view containing options how to export data of a corresponding table view.
 */
const TableExport = (props) => {

    return (
        <div className="table-export">
            <div className="table-export__label">
                <FormattedMessage id="dbm.export.exportAsLabel" values={{
                    tableName: props.label
                }}/>
            </div>
            <div className="table-export__options">
                <TableExportOption icon="doc-pdf" onClick={props.onClickPDF}>
                    {i18n("dbm.export.pdf")}
                </TableExportOption>
                <TableExportOption icon="doc-csv" onClick={props.onClickCSV}>
                    {i18n("dbm.export.csv")}
                </TableExportOption>
                <TableExportOption icon="doc-xlsx" onClick={props.onClickXLSX}>
                    {i18n("dbm.export.xlsx")}
                </TableExportOption>
            </div>
        </div>
    );
};

/**
 * This is one option element for the <TableExport />
 */
export const TableExportOption = (props) => {
    return (
        <div className="table-export__options__option" style={props.style} onClick={props.onClick}>
            {props.icon ? <Icon name={props.icon}/> : null}
            {props.children}
        </div>
    );
};

export const AverageRatesExport = (props) => {

    const { locale, onClickPDF, onClickXLSX, ...rest } = props;
    const years = getYears(true);
    let year = years[0];

    return (
        <div className="table-export" {...rest}>
            <div className="table-export__label" dangerouslySetInnerHTML={{__html: "<span>"+i18nString("dbm.export.yearlyAverageRates", locale)+"</span>"}} />
            <div className="table-export__options">
                <TableExportOption>
                    <div className="dropdown__boxitem">
                        <Dropdown
                            data={years}
                            defaultValue={year}
                            onChange={newValue => { year = newValue; }}
                            aria-label={i18nString('dbm.export.selectYear', locale)}
                        />
                    </div>
                </TableExportOption>
                <TableExportOption icon="doc-pdf" onClick={() => {
                    onClickPDF(year)
                }}>
                    {i18n("dbm.export.pdf")}
                </TableExportOption>
                <TableExportOption icon="doc-xlsx" onClick={() => {
                    onClickXLSX(year)
                }}>
                    {i18n("dbm.export.xlsx")}
                </TableExportOption>
            </div>
        </div>
    );
};

export class MonthlyExport extends React.Component {

    state = {};

    render() {
        const { onClickPDF, onClickXLSX, locale, label, ...rest } = this.props;
        // if the current month is january, skip this year
        const years = getYears(moment().month() === 0);
        let year = this.state.year || years[0];

        let months = getMonths(locale, year);
        let month = getMonthByShortcut(this.state.month || months[months.length-1]);

        return (
            <div className="table-export" style={{height: "7.7rem"}} {...rest}>
                <div className="table-export__label" id={rest['aria-labelledby']} dangerouslySetInnerHTML={{__html: "<span>"+label+"</span>"}} />
                <div className="table-export__options">
                    <TableExportOption>
                        <div className="dropdown__boxitem dropdown__boxitem">
                            <Dropdown
                                data={months}
                                defaultValue={this.state.month || months[months.length-1]}
                                textField={month => get3charMonth(moment, moment(month, "MMM"))}
                                onChange={newValue => {
                                    this.setState({
                                        month: newValue
                                    });
                                }}
                                aria-label={i18nString('dbm.export.selectMonth', locale) + ' ' + label}
                            />
                        </div>
                        <div className="dropdown__boxitem dropdown__boxitem">
                            <Dropdown
                                data={years}
                                defaultValue={this.state.year || years[0]}
                                onChange={newValue => {
                                    this.setState({
                                        year: newValue
                                    });
                                }}
                                aria-label={i18nString('dbm.export.selectYear', locale) + ' ' + label}
                            />
                        </div>
                    </TableExportOption>
                    <TableExportOption style={{lineHeight: "7.7rem"}} icon="doc-pdf" onClick={() => {
                        onClickPDF(year, month)
                    }}>
                        {i18n("dbm.export.pdf")}
                    </TableExportOption>
                    <TableExportOption style={{lineHeight: "7.7rem"}} icon="doc-xlsx" onClick={() => {
                        onClickXLSX(year, month)
                    }}>
                        {i18n("dbm.export.xlsx")}
                    </TableExportOption>
                </div>
            </div>
        );
    }
};

TableExport.propTypes = {
    onClickPDF: PropTypes.func.isRequired,
    onClickCSV: PropTypes.func.isRequired,
    onClickXLSX: PropTypes.func.isRequired,
    label: PropTypes.node.isRequired
};

TableExportOption.propTypes = {

    /**
     * this is content of the option
     */
    children: PropTypes.node,

    /**
     * this is the options icon
     */
    icon: PropTypes.string,

    /**
     * The click handler gets called when that option is clicked
     */
    onClick: PropTypes.func
};

export default TableExport;